import React, { useCallback, useEffect, useState } from 'react';

import { EMAIL_SENDER } from '../constantsConfig/constantsConfig';
import { apiService } from '../services/api.services';
import type { UseLoginOptions, UseLoginReturn } from '../types';
import type { Contato } from '../types/apiType';
import type { VinculoEstipulanteItem } from '../types/estipulanteVinculoType';
import { generateSixDigitCode } from '../utils/codeGenerator';
import { isCelular } from '../utils/phoneUtils';
import { persistCpfHash } from '../utils/authHash';
import { clearEntraIdIdentity } from '../utils/entraIdSession';

import { useCompanyStore } from '../store/companyStore';

import { useAuth } from './useAuth';

/** User-facing message when business-links API fails. */
/** User-facing message  when contacts API fails. */
export const LOGIN_ERROR_CONTACTS_LIST = 'Não foi possível listar a lista de contatos.';

export const useLogin = (options?: UseLoginOptions, initialUserId?: number): UseLoginReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isCodeExpired, setIsCodeExpired] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string[]>(['', '', '', '', '', '']);
  const [showCodeFields, setShowCodeFields] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [maskedCellphone, setMaskedCellphone] = useState<string | null>(null);
  const [idPessoa, setIdPessoa] = useState<string | null>(null);
  /** Business links from LISTAR_VINCULO_NEGOCIO after verifyUser; used for post-login company selection (never skip straight to home). */
  const [vinculos, setVinculos] = useState<VinculoEstipulanteItem[]>([]);
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [cpfLogin, setCpfLogin] = useState<string | null>(null);
  const [vinculosRequestFailed, setVinculosRequestFailed] = useState(false);
  const [contatosRequestFailed, setContatosRequestFailed] = useState(false);

  const { setAuthData, clearError: clearAuthError } = useAuth();
  const { onSuccess, onFormClear, onSecondStepRequired, skipSecondStep } = options || {};

  // Set initial userId if provided
  React.useEffect(() => {
    if (initialUserId && initialUserId !== userId) {
      setUserId(initialUserId);
    }
  }, [initialUserId, userId]);

  // Timer countdown effect
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [timeLeft]);

  const clearError = useCallback(() => {
    setError(null);
    setVinculosRequestFailed(false);
    setContatosRequestFailed(false);
    clearAuthError();
  }, [clearAuthError]);

  const notifySecondStepFromContacts = useCallback(
    (pessoaId: string, contatosCarregados: Contato[]) => {
      setContatos(contatosCarregados);
      setContatosRequestFailed(false);
      const emails = contatosCarregados.filter(c => c.email !== null);
      const telefones = contatosCarregados.filter(c => c.telefone !== null && isCelular(c.telefone));
      const maskedEmailVal = emails.length > 0 ? emails[0].email : null;
      const maskedCellphoneVal = telefones.length > 0 ? telefones[0].telefone : null;
      setMaskedEmail(maskedEmailVal);
      setMaskedCellphone(maskedCellphoneVal);
      if (onSecondStepRequired) {
        const tempUserId = parseInt(pessoaId, 10) || 0;
        setUserId(tempUserId);
        onSecondStepRequired(
          tempUserId,
          emails.length > 0,
          telefones.length > 0,
          maskedEmailVal,
          maskedCellphoneVal,
        );
      }
    },
    [onSecondStepRequired],
  );

  const completeLoginFinal = useCallback(async (cpf: string): Promise<boolean> => {
    try {
      const response = await apiService.login.loginFinal(cpf);

      if (!response) {
        throw new Error('Não foi possível concluir o login');
      }

      const pessoaFisica = response.pessoaFisicaResponse?.pessoaFisica;
      if (!pessoaFisica) {
        throw new Error('Dados do usuário não encontrados');
      }

      const userData = {
        idPessoa: parseInt(response.idPessoa.toString(), 10),
        cpf: response.cpf,
        pessoaFisicaResponse: response.pessoaFisicaResponse,
      };

      persistCpfHash(response.cpf);
      clearEntraIdIdentity();

      if (onSuccess) {
        onSuccess({ redirectToCompanySelection: true });
      }

      setAuthData({ user: userData });
      if (onFormClear) onFormClear();

      return true;
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Não foi possível concluir o login');
      }
      return false;
    }
  }, [setAuthData, onSuccess, onFormClear]);

  const loadContactsAndOpenSecondStep = useCallback(
    async (pessoaId: string): Promise<boolean> => {
      let contatosCarregados: Contato[] = [];
      try {
        const contatosResponse = await apiService.login.getContacts(pessoaId);
        contatosCarregados = contatosResponse?.contatos ?? [];
      } catch (error) {
        const status = error instanceof Error ? (error as Error & { status?: number }).status : undefined;
        if (status === 400) {
          notifySecondStepFromContacts(pessoaId, []);
          return true;
        }
        setContatosRequestFailed(true);
        setError(LOGIN_ERROR_CONTACTS_LIST);
        return false;
      }
      notifySecondStepFromContacts(pessoaId, contatosCarregados);
      return true;
    },
    [notifySecondStepFromContacts],
  );

  const login = useCallback(async (cpf: string, userkey: string): Promise<boolean> => {
    if (!cpf || !userkey) {
      setError('CPF e senha são obrigatórios');
      return false;
    }

    setIsLoading(true);
    setError(null);
    setVinculosRequestFailed(false);
    setContatosRequestFailed(false);
    clearAuthError();

    try {
      const cleanCpf = cpf.replace(/\D/g, '');

      // 1. Verify user — idPessoa (and optional cpf) until login-final (CPF + user key encrypted in loginService)
      const verifyResponse = await apiService.login.verifyUser(cleanCpf, userkey);

      if (!verifyResponse || !verifyResponse.idPessoa) {
        throw new Error('Usuário ou senha inválidos');
      }

      const cpfParaLoginFinal = verifyResponse.cpf?.replace(/\D/g, '') || cleanCpf;
      setCpfLogin(cpfParaLoginFinal);

      const pessoaId = verifyResponse.idPessoa;
      setIdPessoa(pessoaId);

      // 2. clear company session; vínculos are now loaded by company store on CompanyPage.
      useCompanyStore.getState().clearCompanySession();
      setVinculos([]);
      setVinculosRequestFailed(false);

      // 3. Skip 2FA quando skipSecondStep está ativo; senão Contacts → 2FA
      if (skipSecondStep) {
        return await completeLoginFinal(cpfParaLoginFinal);
      }

      const ok = await loadContactsAndOpenSecondStep(pessoaId);
      if (!ok) {
        return false;
      }

      return false; // Login not complete until login-final
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Não foi possível realizar o login');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [clearAuthError, loadContactsAndOpenSecondStep, skipSecondStep, completeLoginFinal]);

  const retryBusinessLinks = useCallback(async (): Promise<boolean> => {
    // Kept for compatibility with LoginForm flow; now it retries only contacts step.
    if (!idPessoa) {
      setError('ID da pessoa não encontrado');
      return false;
    }
    setIsLoading(true);
    setError(null);
    setVinculosRequestFailed(false);
    setContatosRequestFailed(false);
    clearAuthError();
    try {
      return await loadContactsAndOpenSecondStep(idPessoa);
    } finally {
      setIsLoading(false);
    }
  }, [idPessoa, clearAuthError, loadContactsAndOpenSecondStep]);

  const retryLoginContacts = useCallback(async (): Promise<boolean> => {
    if (!idPessoa) {
      setError('ID da pessoa não encontrado');
      return false;
    }
    setIsLoading(true);
    setError(null);
    setContatosRequestFailed(false);
    clearAuthError();
    try {
      const ok = await loadContactsAndOpenSecondStep(idPessoa);
      return ok;
    } finally {
      setIsLoading(false);
    }
  }, [idPessoa, clearAuthError, loadContactsAndOpenSecondStep]);

  const resetState = useCallback(() => {
    setError(null);
    setTimeLeft(0);
    setIsCodeExpired(false);
    setVerificationCode(['', '', '', '', '', '']);
    setShowCodeFields(false);
    setCodeSent(false);
    setIsLoading(false);
    setUserId(null);
    setMaskedEmail(null);
    setMaskedCellphone(null);
    setIdPessoa(null);
    setVinculos([]);
    setContatos([]);
    setGeneratedCode(null);
    setCpfLogin(null);
    setVinculosRequestFailed(false);
    setContatosRequestFailed(false);
  }, []);

  const resetVerificationState = useCallback(() => {
    setError(null);
    setTimeLeft(0);
    setIsCodeExpired(false);
    setVerificationCode(['', '', '', '', '', '']);
    setShowCodeFields(false);
    setCodeSent(false);
    setIsLoading(false);
    setGeneratedCode(null);
    // Keep userId, idPessoa, maskedEmail, maskedCellphone, vinculos, and contatos
  }, []);

  const updateCode = useCallback((index: number, value: string) => {
    if (value.length > 1) return; // Only one digit per field

    setVerificationCode(prev => {
      const newCode = [...prev];
      newCode[index] = value;
      return newCode;
    });
    setError(null);
  }, []);

  const sendCode = useCallback(async (email: boolean, phone: boolean): Promise<boolean> => {
    if (!idPessoa) {
      setError('ID da pessoa não encontrado'); // invariant: idPessoa set in verifyUser
      return false;
    }

    // Validate that exactly one option is selected (XOR logic)
    if (email === phone) {
      setError('Selecione uma das opções de validação');
      return false;
    }

    // Check if timer is active
    if (timeLeft > 0) {
      setError('Aguarde o tempo limite para reenviar o código');
      return false;
    }

    setIsLoading(true);
    setError(null);
    setIsCodeExpired(false);
    setShowCodeFields(false);

    // Clear verification code fields
    setVerificationCode(['', '', '', '', '', '']);

    try {
      // Generate 6 digit code
      const code = generateSixDigitCode();
      setGeneratedCode(code);

      if (email) {
        // Find the first email in the contacts
        const emailContato = contatos.find(c => c.email !== null);
        if (!emailContato || !emailContato.email) {
          throw new Error('Não foi possível enviar e-mail');
        }

        // Send email with code
        await apiService.login.sendEmail({
          from: EMAIL_SENDER,
          to: emailContato.email,
          subject: 'Confirmação',
          message: `Portal do Estipulante - Seu código é: ${code}`,
        });
      } else if (phone) {
        // First mobile contact for SMS
        const telefoneContato = contatos.find(c => c.telefone !== null && isCelular(c.telefone));
        if (!telefoneContato || !telefoneContato.telefone) {
          throw new Error('Não foi possível enviar SMS');
        }

        // Send SMS with code
        await apiService.login.sendSms(telefoneContato.telefone, `Portal do Estipulante - Seu código é: ${code}`);
      }

      setTimeLeft(60);
      setCodeSent(true);
      setShowCodeFields(true);
      return true;
    } catch (error) {
      setShowCodeFields(false);
      setGeneratedCode(null);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Não foi possível enviar o código');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [timeLeft, idPessoa, contatos]);

  const validateCode = useCallback(async (code: string[]): Promise<boolean> => {
    if (!idPessoa) {
      setError('ID da pessoa não encontrado');
      return false;
    }

    // Mark code as expired if timer has reached zero
    if (timeLeft === 0 && !isCodeExpired) {
      setIsCodeExpired(true);
    }

    const isComplete = code.every(digit => digit !== '');
    if (!isComplete || isCodeExpired || timeLeft === 0) {
      setError('Código incompleto ou expirado');
      return false;
    }

    // Validate the generated code locally
    const codeString = code.join('');
    if (codeString !== generatedCode) {
      setError('Código de verificação incorreto');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (!cpfLogin) {
        throw new Error('CPF não encontrado');
      }

      return await completeLoginFinal(cpfLogin);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Código de verificação incorreto');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isCodeExpired, timeLeft, idPessoa, generatedCode, cpfLogin, completeLoginFinal]);

  const resendCode = useCallback(async (email: boolean, phone: boolean): Promise<boolean> => {
    // Check if timer is active
    if (timeLeft > 0) {
      setError('Aguarde o tempo limite para reenviar o código');
      return false;
    }

    // Call the same function as send code
    return await sendCode(email, phone);
  }, [timeLeft, sendCode]);

  // Send code to a specific contact (email or phone)
  const sendCodeToContact = useCallback(async (contato: Contato): Promise<boolean> => {
    if (!idPessoa) {
      setError('ID da pessoa não encontrado');
      return false;
    }

    // Check if timer is active
    if (timeLeft > 0) {
      setError('Aguarde o tempo limite para reenviar o código');
      return false;
    }

    setIsLoading(true);
    setError(null);
    setIsCodeExpired(false);
    setShowCodeFields(false);

    // Clear verification code fields
    setVerificationCode(['', '', '', '', '', '']);

    try {
      // Generate 6 digit code
      const code = generateSixDigitCode();
      setGeneratedCode(code);

      if (contato.email) {
        // Send email with code
        await apiService.login.sendEmail({
          from: EMAIL_SENDER,
          to: contato.email,
          subject: 'Confirmação',
          message: `Seu código é ${code}`,
        });
      } else if (contato.telefone) {
        // Send SMS with code
        await apiService.login.sendSms(contato.telefone, `Portal do Estipulante - Seu código é: ${code}`);
      } else {
        throw new Error('Contato inválido: deve ter email ou telefone');
      }

      setTimeLeft(60);
      setCodeSent(true);
      setShowCodeFields(true);
      return true;
    } catch (error) {
      setShowCodeFields(false);
      setGeneratedCode(null);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Erro ao enviar código de verificação');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [timeLeft, idPessoa]);

  // Fetch contacts if not already loaded (empty list is valid; only API failure blocks)
  const fetchContacts = useCallback(async (): Promise<Contato[]> => {
    if (!idPessoa) {
      throw new Error('ID da pessoa não encontrado');
    }

    if (contatos.length > 0 && !contatosRequestFailed) {
      return contatos;
    }

    setIsLoading(true);
    setError(null);
    setContatosRequestFailed(false);

    try {
      const contatosResponse = await apiService.login.getContacts(idPessoa);
      const list = contatosResponse?.contatos ?? [];
      setContatos(list);
      setContatosRequestFailed(false);
      const emails = list.filter(c => c.email !== null);
      const telefones = list.filter(c => c.telefone !== null && isCelular(c.telefone));
      setMaskedEmail(emails.length > 0 ? emails[0].email : null);
      setMaskedCellphone(telefones.length > 0 ? telefones[0].telefone : null);
      return list;
    } catch (error) {
      const status = error instanceof Error ? (error as Error & { status?: number }).status : undefined;
      if (status === 400) {
        const list: Contato[] = [];
        setContatos(list);
        setContatosRequestFailed(false);
        setMaskedEmail(null);
        setMaskedCellphone(null);
        return list;
      }
      setContatosRequestFailed(true);
      setError(LOGIN_ERROR_CONTACTS_LIST);
      throw new Error(LOGIN_ERROR_CONTACTS_LIST);
    } finally {
      setIsLoading(false);
    }
  }, [idPessoa, contatos, contatosRequestFailed]);

  return {
    isLoading,
    error,
    login,
    clearError,
    // Verification code methods
    timeLeft,
    isCodeExpired,
    verificationCode,
    showCodeFields,
    codeSent,
    sendCode,
    sendCodeToContact,
    fetchContacts,
    validateCode,
    resendCode,
    updateCode,
    resetState,
    resetVerificationState,
    userId,
    setUserId,
    // Masked data for display
    maskedEmail,
    maskedCellphone,
    // Contacts list
    contatos,
    // Business links for company selection after 2FA
    vinculos,
    vinculosRequestFailed,
    contatosRequestFailed,
    retryBusinessLinks,
    retryLoginContacts,
  };
};
